import React, { ReactNode } from "react";
import {
  PhoneType,
  VerificationType,
} from "../components/UI/KYC/VerificationButton/VerificationButton";
import { SxProps, Theme } from "@mui/material";

/*
    name of the field, can be nested, e.g. 'field1.field2.field3'
    initial value for the field
    label for the field
    text, number, date, time, datetime, file, image, color, password, email ...

    {xs, sm, md, lg, xl}
    options for select input
    selector for select input
*/
export interface IFormField {
  ref?: any;
  name: string;
  initailValue: any;
  initailValueReadPath?: string;
  label: string | React.ReactNode | any;
  type?: string;
  uiType: string;
  uiBreakpoints: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  isDisabled?: boolean;
  isHidden?: boolean;

  // for select
  options?: any[];
  selector?: {
    value: (option: any) => any;
    label: (option: any) => any;
  };

  // for date picker
  dateFormat?: string;
  maxDate?: Date;
  minDate?: Date;
  applyDateLowerLimit?: boolean;
  applyDateUpperLimit?: boolean;
  views?: Array<string>;

  // for phone number
  customFormat?: string;

  // for client account
  multipleAccounts?: boolean;
  eligible_for_loan_application?: boolean;
  withdraw_enabled?: boolean;
  ignore_branch?: boolean;
  branch?: string;

  // for text input
  multiline?: boolean;
  rows?: number;
  decimalPlaces?: number;
  max?: number;
  min?: number;
  value?: any;

  // for select search
  parseFilter?: (...params: any) => any;
  filterValues?: any;
  dataFetcher?: (
    search: string,
    setData: (results: any) => void,
    extraFilters?: any,
  ) => void;
  filterCriteria?: string;

  // for file upload
  accepts?: string; // comma separated string eg. 'image/png, image/jpeg'
  preview?: string;
  isFixed?: boolean;
  stencilShape?: "circle" | "rectangle";
  multipleFiles?: boolean;

  // radio btn
  radioOptions?: any[];

  // groups
  isGrouped?: boolean;
  parentInput?: IFormField | any;
  secondaryInput?: IFormField | any;
  placeHolder?: string;
  setClientAccountDetails?: (details: any) => void;
  setLoadingClientAccountDetails?: (value: boolean) => void;

  // KYC
  withVerification?: boolean;
  verificationType?: VerificationType;
  phoneVerificationType?: PhoneType;

  // styles
  styles?: SxProps<Theme> | undefined;
  // handlers
  onFocus?: () => void;
  countryCode?: string;
  setPhoneCode?: (code: any) => void;
  hint?: ReactNode | string;
  isNotConfigurable?: boolean;
  showOnFormConfiguration?: boolean;
  hideOnFormConfiguration?: boolean;

  //for fee table field
  feesData?: any[];
  modalId?: string;
  drawerZIndex?: number;
}

interface NestedObject {
  [key: string]: NestedObject | string;
}

export function deepMerge<T extends NestedObject>(target: T, source: T): T {
  for (const key of Object.keys(source)) {
    if (typeof source[key] === "object" && source[key] !== null) {
      if (!target[key]) {
        (target as any)[key] = {} as T[keyof T];
      }
      if (typeof target[key] === "object" && target[key] !== null) {
        deepMerge((target as any)[key], source[key]);
      }
    } else {
      (target as any)[key] = source[key];
    }
  }
  return target;
}

export const getInitialValues = (formFields: IFormField[]) => {
  return {
    ...formFields.reduce((acc1: any, field: IFormField) => {
      if (field.name.includes(".")) {
        const fieldNames = field.name.split(".");

        const nestedObject = fieldNames.reduceRight(
          (acc: any, fieldName: string, currentIndex: number) => {
            if (acc && currentIndex < fieldNames.length - 1) {
              return { [fieldName]: acc };
            }
            return { [fieldName]: field.initailValue };
          },
          {},
        );

        if (!acc1[fieldNames[0]]) {
          return { ...acc1, ...nestedObject };
        } else {
          return deepMerge(acc1, nestedObject);
        }
      }
      return { ...acc1, [field.name]: field.initailValue };
    }, {}),
  };
};

export const deepDerefrencer = (obj: any, path?: string) => {
  return path?.split(".").reduce((acc: any, field: string) => {
    if (acc) {
      return acc[field];
    }
    return undefined;
  }, obj);
};

export const deepDerefrencerValidations = (obj: any, path: string) => {
  return path?.split(".")?.reduce((acc: any, field: string) => {
    if (acc) {
      return acc?.fields?.[field];
    }
    return undefined;
  }, obj);
};

const setFallbackValue = (value: any, fallback: any) => {
  if (value === undefined || value === null) {
    return fallback;
  }
  return value;
};

export const patchInitialValues = (formFields: Array<IFormField>) => {
  return (initialValues: any) => {
    return {
      ...formFields.reduce((acc1: any, field: IFormField) => {
        if (field.name.includes(".")) {
          const fieldNames = field.name.split(".");
          const nestedObject = fieldNames.reduceRight(
            (acc: any, fieldName: string, currentIndex: number) => {
              if (acc && currentIndex < fieldNames.length - 1) {
                return { [fieldName]: acc };
              }
              const path = field.initailValueReadPath
                ? field.initailValueReadPath
                : fieldNames.slice(0, currentIndex + 1).join(".");
              const value = setFallbackValue(
                deepDerefrencer(initialValues, path),
                field.initailValue,
              );
              return { [fieldName]: value };
            },
            {},
          );

          if (!acc1[fieldNames[0]]) {
            return { ...acc1, ...nestedObject };
          } else {
            return deepMerge(acc1, nestedObject);
          }
        }
        return {
          ...acc1,
          [field.name]: setFallbackValue(
            deepDerefrencer(
              initialValues,
              field.initailValueReadPath || field.name,
            ),
            field.initailValue,
          ),
        };
      }, {}),
    };
  };
};

export const getFieldByName = (
  formFields: IFormField[],
  fieldName: string,
): IFormField => {
  return (formFields.find((field) => field.name === fieldName) ||
    {}) as IFormField;
};

export const getDecimalPlaces = (decimalPlaces?: number): number => {
  return decimalPlaces !== undefined ? decimalPlaces : 2;
};

export const createFormData = (data: any, parentKey = "") => {
  const formData = new FormData();

  function appendFormData(obj: any, parent: any) {
    Object.keys(obj).forEach((key) => {
      const fullKey = parent ? `${parent}${key}` : key;
      const value = obj[key];

      if (value instanceof Array) {
        value.forEach((item, index) => {
          const arrayKey = `${fullKey}[${index}]`;
          if (typeof item === "object") {
            appendFormData(item, arrayKey); // Recursively handle objects within the array
          } else {
            formData.append(arrayKey, item);
          }
        });
      } else if (
        typeof value === "object" &&
        value !== null &&
        !(value instanceof File)
      ) {
        appendFormData(value, fullKey); // Recursively handle nested objects
      } else {
        formData.append(fullKey, value); // Append primitive values
      }
    });
  }

  appendFormData(data, parentKey);
  return formData;
};
